import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { logoutThunk, mergeUser } from '@/store/slices/authSlice'
import { fetchMyProfile } from '@/services/userService'
import { BrandMark } from '@/components/layout/BrandMark'
// import { RoadmapComingSoonModal } from '@/components/dashboard/RoadmapComingSoonModal'
import { PitchDeckComingSoonModal } from '@/components/layout/PitchDeckComingSoonModal'
import {
  goldGradientButtonSm,
  outlineButton,
  ctaRadius,
} from '@/lib/brandGradient'
import { useTheme } from '@/hooks/useTheme'
import { usePrefersReducedMotion, expandCollapseProps } from '@/lib/motion'
import { cn } from '@/lib/cn'
import { lockBodyScroll } from '@/lib/bodyScrollLock'
import { buildLoginUrlWithRedirect } from '@/lib/postLoginRedirect'

const navItems = ['Ideas', 'Dashboard', 'Leaderboard'] as const

type NavItemLabel = (typeof navItems)[number]

/** Match nav item to current URL, including dashboard/leaderboard sub-routes. */
function pathMatchesNavItem(label: NavItemLabel, pathname: string): boolean {
  if (label === 'Ideas') {
    return (
      pathname === '/validate-ideas' || pathname.startsWith('/validate-ideas/')
    )
  }
  if (label === 'Leaderboard') {
    return pathname === '/leaderboard' || pathname.startsWith('/leaderboard/')
  }
  if (label === 'Dashboard') {
    return pathname === '/dashboard' || pathname.startsWith('/dashboard/')
  }
  return false
}

/** Ideas and Leaderboard are public reads, so a guest can reach them the same
 *  way a signed-in user does. Dashboard is the only nav item still behind the
 *  sign-in wall. */
function isReachableByGuest(label: NavItemLabel): boolean {
  return label !== 'Dashboard'
}

function HeaderNavLink({
  label,
  variant,
  active,
  isAuthenticated,
  onNavigate,
}: {
  label: NavItemLabel
  variant: 'desktop' | 'mobile'
  active: boolean
  isAuthenticated: boolean
  onNavigate: (label: NavItemLabel) => void
}) {
  const reachable = isAuthenticated || isReachableByGuest(label)
  const bottom = variant === 'desktop' ? 'bottom-1' : 'bottom-2'
  const shell =
    variant === 'desktop'
      ? 'group relative shrink-0 rounded-full px-4 py-2 text-[15px] transition-colors duration-200'
      : 'group relative w-full rounded-xl px-4 py-3 text-left text-[15px] transition-colors duration-200'

  const showAsActive = active && reachable

  const text = showAsActive
    ? 'font-semibold text-[#e8c995]'
    : reachable
      ? 'font-semibold text-[var(--foreground)]'
      : 'font-medium text-[var(--foreground-muted)] group-hover:text-[var(--foreground)]'

  const displayLabel = label === 'Dashboard' ? 'My Dashboard' : label

  return (
    <button
      type="button"
      className={cn(shell, text)}
      aria-current={showAsActive ? 'page' : undefined}
      onClick={() => onNavigate(label)}
    >
      <span className="relative z-10">{displayLabel}</span>
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 z-0 block h-0.5 rounded-full bg-[#E0A263]',
          'origin-center transition-transform duration-500 will-change-transform',
          'ease-[cubic-bezier(0.22,1,0.36,1)]',
          'motion-reduce:transition-none motion-reduce:duration-0',
          bottom,
          showAsActive
            ? 'scale-x-100'
            : cn('scale-x-0', reachable && 'group-hover:scale-x-100'),
        )}
      />
    </button>
  )
}

function PitchOSNavLink({
  variant,
  onOpen,
}: {
  variant: 'desktop' | 'mobile'
  onOpen: () => void
}) {
  const bottom = variant === 'desktop' ? 'bottom-1' : 'bottom-2'
  const shell =
    variant === 'desktop'
      ? 'group relative shrink-0 rounded-full px-4 py-2 text-[15px] font-semibold text-[var(--foreground)] transition-colors duration-200'
      : 'group relative w-full rounded-xl px-4 py-3 text-left text-[15px] font-semibold text-[var(--foreground)] transition-colors duration-200'

  return (
    <button type="button" className={cn(shell)} onClick={onOpen}>
      <span className="relative z-10">Pitch OS</span>
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-x-0 z-0 block h-0.5 rounded-full bg-[#E0A263]',
          'origin-center scale-x-0 transition-transform duration-500 will-change-transform',
          'ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100',
          'motion-reduce:transition-none motion-reduce:duration-0',
          bottom,
        )}
      />
    </button>
  )
}

const MenuIcon = () => (
  <svg
    aria-hidden
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
  </svg>
)

const CloseIcon = () => (
  <svg
    aria-hidden
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
  </svg>
)

export const Header = () => <HeaderInner />

const LogoutDoorIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"
    />
  </svg>
)

function LogoutConfirmModal({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  useEffect(() => {
    if (!open || typeof document === 'undefined') return
    return lockBodyScroll()
  }, [open])

  if (!open || typeof document === 'undefined') return null

  /** Portal to `body` so `position:fixed` centers on the viewport (not warped by sticky header / backdrop parents). */
  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/65 px-4 py-8 backdrop-blur-[12px]"
      style={{ minHeight: '100dvh' }}
      role="presentation"
      onClick={onCancel}
    >
      {/* Click-shield stops overlay's onCancel from firing when clicking inside the dialog. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-confirm-title"
        className={cn(
          'box-border flex w-[448px] max-w-[min(448px,calc(100vw-2rem))] flex-col items-center rounded-2xl border border-[var(--glass-border-soft)] bg-[var(--surface-card)] p-6 text-center shadow-2xl',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-center text-red-500">
          <LogoutDoorIcon className="h-10 w-10" />
        </div>
        <h2
          id="logout-confirm-title"
          className="mt-3 shrink-0 text-lg font-medium tracking-tight text-[var(--foreground)]"
        >
          Leaving Already?
        </h2>
        <p className="mt-2 max-w-[360px] shrink-0 text-[13px] leading-relaxed text-[var(--foreground-muted)]">
          You can always pick up where you left off.
        </p>
        <div className="mt-6 flex w-full shrink-0 flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={onCancel}
            className={cn(
              'flex-1 py-2.5 text-sm font-semibold',
              ctaRadius,
              outlineButton,
            )}
          >
            Stay Signed In
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`flex-1 bg-red-600 py-2.5 text-sm font-semibold text-white transition hover:bg-red-500 ${ctaRadius}`}
          >
            Log Out
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

const HeaderInner = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)
  const authUser = useAppSelector((s) => s.auth.user)
  const userEmail = authUser?.email
  const userHandle = authUser?.handle ?? null
  const userFullName = authUser?.full_name ?? null
  const isAdmin = authUser?.is_admin === true
  // Whether we have actually loaded the admin flag yet (vs it just being absent
  // on an older session) — drives the /user/me backfill.
  const isAdminKnown = typeof authUser?.is_admin === 'boolean'
  const userInitial =
    (userFullName && userFullName.trim().charAt(0).toUpperCase()) ||
    (userEmail ? userEmail.charAt(0).toUpperCase() : null)
  const reduceMotion = usePrefersReducedMotion()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [logoutModalOpen, setLogoutModalOpen] = useState(false)
  // const [roadmapModalOpen, setRoadmapModalOpen] = useState(false)
  const [pitchDeckModalOpen, setPitchDeckModalOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Reset transient menu UI whenever the route changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMobileOpen(false)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUserMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileOpen])

  // Backfill handle / full_name / is_admin from /user/me when missing — older
  // sessions and some auth flows don't include them in the login response.
  useEffect(() => {
    if (!isAuthenticated) return
    if (userHandle && userFullName && isAdminKnown) return
    let cancelled = false
    fetchMyProfile()
      .then((me) => {
        if (cancelled) return
        const patch: Partial<{
          handle: string | null
          full_name: string | null
          is_admin: boolean
        }> = {}
        if (me.handle && me.handle !== userHandle) patch.handle = me.handle
        if (me.full_name && me.full_name !== userFullName)
          patch.full_name = me.full_name
        if (me.is_admin !== isAdmin) patch.is_admin = me.is_admin
        if (Object.keys(patch).length > 0) dispatch(mergeUser(patch))
      })
      .catch(() => {
        /* not fatal — header still renders, "View Profile" stays hidden */
      })
    return () => {
      cancelled = true
    }
  }, [
    isAuthenticated,
    userHandle,
    userFullName,
    isAdminKnown,
    isAdmin,
    dispatch,
  ])

  useEffect(() => {
    if (!userMenuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      )
        setUserMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [userMenuOpen])

  const goNav = (label: NavItemLabel) => {
    if (label === 'Ideas') return navigate('/validate-ideas')
    if (label === 'Leaderboard') return navigate('/leaderboard')
    if (label !== 'Dashboard') return

    // Dashboard is the caller's own workspace, so it still needs a session.
    // Send guests through login with a return path rather than dropping them
    // on the homepage.
    if (!isAuthenticated) {
      navigate(buildLoginUrlWithRedirect('/dashboard', '', ''))
      return
    }
    navigate('/dashboard')
  }

  const openUserMenu = () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    setUserMenuOpen((o) => !o)
  }

  const goProfileFromMenu = () => {
    if (!userHandle) return
    setUserMenuOpen(false)
    navigate(`/users/${encodeURIComponent(userHandle)}`)
  }

  const goReportReviewFromMenu = () => {
    setUserMenuOpen(false)
    navigate('/admin/report-review')
  }

  const openLogoutModal = () => {
    setUserMenuOpen(false)
    setLogoutModalOpen(true)
  }

  const confirmLogout = () => {
    setLogoutModalOpen(false)
    void dispatch(logoutThunk()).finally(() => {
      navigate('/')
    })
  }

  const isActive = (label: NavItemLabel) =>
    pathMatchesNavItem(label, location.pathname)

  return (
    <header className="glass-header">
      <div className="relative mx-auto flex w-full max-w-[1600px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <BrandMark />

        {/* Absolutely centered on the header so the nav sits dead-center of the
            viewport regardless of how wide the brand or right-hand cluster get. */}
        <nav
          className="pointer-events-none absolute inset-x-0 hidden justify-center md:flex"
          aria-label="Main navigation"
        >
          <div className="pointer-events-auto flex flex-nowrap items-center gap-3 lg:gap-4">
            {navItems.map((label) => (
              <HeaderNavLink
                key={label}
                label={label}
                variant="desktop"
                active={isActive(label)}
                isAuthenticated={isAuthenticated}
                onNavigate={goNav}
              />
            ))}
            {isAuthenticated ? (
              <PitchOSNavLink
                variant="desktop"
                onOpen={() => setPitchDeckModalOpen(true)}
              />
            ) : null}
          </div>
        </nav>

        <div className="flex shrink-0 items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={
              theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
            }
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[var(--glass-border)] bg-[var(--glass-face)] text-[var(--foreground-muted)] transition hover:bg-[var(--surface-card)]"
          >
            {theme === 'dark' ? (
              <svg
                aria-hidden
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="4"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg
                aria-hidden
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          {/* {isAuthenticated && (
            <button
              type="button"
              onClick={() => setRoadmapModalOpen(true)}
              className={`hidden rounded-[20px] px-3 py-2 text-[12px] font-semibold text-[var(--foreground-muted)] transition hover:text-[var(--foreground)] sm:px-4 sm:text-[13px] md:mr-1 md:inline-flex ${outlineButton}`}
            >
              View Roadmap
            </button>
          )} */}
          {!isAuthenticated && (
            <button
              type="button"
              onClick={() => setPitchDeckModalOpen(true)}
              className={cn(
                'hidden shrink-0 px-3 py-2 text-[12px] font-semibold whitespace-nowrap sm:px-4 sm:text-[13px] md:inline-flex',
                ctaRadius,
                outlineButton,
              )}
            >
              Pitch OS
            </button>
          )}
          <button
            type="button"
            className="header-hit grid h-10 w-10 place-items-center rounded-lg text-[var(--foreground)] transition hover:bg-black/5 md:hidden dark:hover:bg-white/5"
            aria-expanded={mobileOpen}
            aria-controls="mobile-main-nav"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </button>

          <div ref={userMenuRef} className="relative">
            <button
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
              aria-label={isAuthenticated ? 'Account menu' : 'Sign in'}
              className={cn(
                'header-hit transition',
                isAuthenticated
                  ? 'grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-[#F6CB86] to-[#E0A263] text-black ring-1 ring-[#F6CB86]/40 hover:brightness-110'
                  : cn(
                      'inline-flex items-center justify-center px-4 py-2 text-[13px] whitespace-nowrap',
                      goldGradientButtonSm,
                    ),
              )}
              type="button"
              onClick={openUserMenu}
            >
              {isAuthenticated && userInitial ? (
                <span aria-hidden className="text-sm font-bold tracking-tight">
                  {userInitial}
                </span>
              ) : (
                'Log In'
              )}
            </button>

            {isAuthenticated && userMenuOpen ? (
              <div
                role="menu"
                className="absolute top-full right-0 z-50 mt-2 min-w-[200px] overflow-hidden rounded-xl border border-black/10 bg-white py-1 shadow-xl dark:border-white/10 dark:bg-black"
              >
                <button
                  type="button"
                  role="menuitem"
                  disabled={!userHandle}
                  aria-disabled={!userHandle}
                  title={
                    userHandle
                      ? undefined
                      : 'Complete your profile to claim a handle'
                  }
                  className="account-menu-item w-full px-4 py-2.5 text-left text-sm text-[#0A0A0A] transition hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent dark:text-[var(--foreground)] dark:hover:bg-white/[0.06]"
                  onClick={goProfileFromMenu}
                >
                  View Profile
                </button>
                {isAdmin ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="account-menu-item w-full px-4 py-2.5 text-left text-sm text-[#0A0A0A] transition hover:bg-black/[0.04] dark:text-[var(--foreground)] dark:hover:bg-white/[0.06]"
                    onClick={goReportReviewFromMenu}
                  >
                    Report Review
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className="account-menu-item w-full px-4 py-2.5 text-left text-sm text-[#0A0A0A] transition hover:bg-black/[0.04] dark:text-[var(--foreground)] dark:hover:bg-white/[0.06]"
                  onClick={openLogoutModal}
                >
                  Logout
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {mobileOpen ? (
          <motion.div
            id="mobile-main-nav"
            className="glass-header-drawer overflow-hidden px-4 py-3 md:hidden"
            {...expandCollapseProps(reduceMotion)}
          >
            <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
              {navItems.map((label) => (
                <HeaderNavLink
                  key={label}
                  label={label}
                  variant="mobile"
                  active={isActive(label)}
                  isAuthenticated={isAuthenticated}
                  onNavigate={(label) => {
                    setMobileOpen(false)
                    goNav(label)
                  }}
                />
              ))}
              <PitchOSNavLink
                variant="mobile"
                onOpen={() => {
                  setMobileOpen(false)
                  setPitchDeckModalOpen(true)
                }}
              />
              {/* {isAuthenticated && (
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false)
                    setRoadmapModalOpen(true)
                  }}
                  className="group relative w-full rounded-xl px-4 py-3 text-left text-sm font-semibold text-[var(--foreground)] transition-colors duration-200"
                >
                  View Roadmap
                </button>
              )} */}
            </nav>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <LogoutConfirmModal
        open={logoutModalOpen}
        onCancel={() => setLogoutModalOpen(false)}
        onConfirm={confirmLogout}
      />
      <PitchDeckComingSoonModal
        open={pitchDeckModalOpen}
        onClose={() => setPitchDeckModalOpen(false)}
      />
      {/* <RoadmapComingSoonModal
        open={roadmapModalOpen}
        onClose={() => setRoadmapModalOpen(false)}
      /> */}
    </header>
  )
}
